/** Shared CSV/Excel/PDF export helpers for tabular data across the app. */

export interface ExportColumn<T> {
  header: string;
  accessor: (row: T) => string | number;
}

function escapeCsvValue(value: unknown) {
  return `"${String(value ?? "")
    .replace(/\r?\n/g, " ")
    .replace(/"/g, '""')}"`;
}

function escapeHtml(value: unknown) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function downloadBlob(filename: string, content: BlobPart, mimeType: string) {
  const url = URL.createObjectURL(new Blob([content], { type: mimeType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function exportAsCsv<T>(
  filename: string,
  columns: ExportColumn<T>[],
  rows: T[],
) {
  const lines = [
    columns.map((column) => escapeCsvValue(column.header)).join(","),
    ...rows.map((row) =>
      columns.map((column) => escapeCsvValue(column.accessor(row))).join(","),
    ),
  ];
  downloadBlob(filename, lines.join("\n"), "text/csv;charset=utf-8;");
}

export function exportAsExcel<T>(
  filename: string,
  sheetName: string,
  columns: ExportColumn<T>[],
  rows: T[],
) {
  const headerRow = columns
    .map((column) => `<Cell><Data ss:Type="String">${escapeHtml(column.header)}</Data></Cell>`)
    .join("");
  const bodyRows = rows
    .map(
      (row) =>
        `<Row>${columns
          .map((column) => `<Cell><Data ss:Type="String">${escapeHtml(column.accessor(row))}</Data></Cell>`)
          .join("")}</Row>`,
    )
    .join("");
  const workbookXml = `<?xml version="1.0"?>
    <Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet"
      xmlns:o="urn:schemas-microsoft-com:office:office"
      xmlns:x="urn:schemas-microsoft-com:office:excel"
      xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
      <Worksheet ss:Name="${escapeHtml(sheetName)}">
        <Table>
          <Row>${headerRow}</Row>
          ${bodyRows}
        </Table>
      </Worksheet>
    </Workbook>`;
  downloadBlob(filename, workbookXml, "application/vnd.ms-excel;charset=utf-8;");
}

export function exportAsPdf<T>(
  title: string,
  columns: ExportColumn<T>[],
  rows: T[],
) {
  const printWindow = window.open("", "_blank", "width=1200,height=900");
  if (!printWindow) return;

  const headerCells = columns.map((column) => `<th>${escapeHtml(column.header)}</th>`).join("");
  const bodyRows = rows
    .map(
      (row) =>
        `<tr>${columns.map((column) => `<td>${escapeHtml(column.accessor(row))}</td>`).join("")}</tr>`,
    )
    .join("");

  printWindow.document.write(`<!DOCTYPE html>
    <html>
      <head>
        <title>${escapeHtml(title)}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
          h1 { font-size: 20px; margin-bottom: 4px; }
          p.meta { font-size: 12px; color: #6b7280; margin: 0 0 16px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #d1d5db; padding: 8px 10px; text-align: left; }
          th { background: #f3f4f6; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <h1>${escapeHtml(title)}</h1>
        <p class="meta">Generated ${new Date().toLocaleString()} &middot; ${rows.length} record${rows.length === 1 ? "" : "s"}</p>
        <table>
          <thead><tr>${headerCells}</tr></thead>
          <tbody>${bodyRows}</tbody>
        </table>
      </body>
    </html>`);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => printWindow.print(), 500);
}
